// The account page's credits copy (pricing v2, W6). Pure helpers so the
// three honest date lines and the ledger rows can be pinned by tests without
// rendering the whole page.
//
// Tone: every line is a fact about the plan — never urgency, never a pitch.

import {
  FREE_MONTHLY_CREDITS,
  PLANS,
  PLAN_LABELS,
  formatCreditsNumber,
  planProduct,
} from "../../constants/credits";

/** @typedef {import("../../contexts/AccountContext").Credits} Credits */

/**
 * "Oct 1", or "Oct 1, 2027" when the date is not in the current year.
 * @param {string | null | undefined} iso
 * @returns {string} "" when the date is missing or unparseable.
 */
export const formatShortDate = (iso) => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    ...(sameYear ? {} : { year: "numeric" }),
  });
};

/**
 * "Free plan" / "Agent · monthly" / "Pro · annual".
 * @param {string | null | undefined} planKey
 */
export const planLabelFor = (planKey) => {
  const product = planProduct(planKey);
  if (product === "free") return "Free plan";
  const name = PLAN_LABELS[product] || product;
  const plan = planKey ? PLANS[planKey] : undefined;
  if (!plan) return `${name} plan`;
  return `${name} · ${plan.interval === "year" ? "annual" : "monthly"}`;
};

/**
 * The honest date line under the balance:
 *   Free:    "Your next 1,000 credits arrive Oct 1. Unused credits don't carry over."
 *   Monthly: "2,500 credits arrive each month on Oct 1; unused credits don't carry over."
 *   Annual:  "Pool of 36,000 credits, good until Sep 2, 2027."
 * Without a date the same facts are stated without one.
 * @param {Credits} credits
 */
export const creditsDateLine = (credits) => {
  const date = formatShortDate(credits.pool_expires_at);
  const plan = PLANS[credits.plan_key];
  if (!plan) {
    const monthly = formatCreditsNumber(FREE_MONTHLY_CREDITS);
    return date
      ? `Your next ${monthly} credits arrive ${date}. Unused credits don't carry over.`
      : `${monthly} credits arrive each month. Unused credits don't carry over.`;
  }
  const amount = formatCreditsNumber(plan.credits);
  if (plan.pool === "monthly") {
    return date
      ? `${amount} credits arrive each month on ${date}; unused credits don't carry over.`
      : `${amount} credits arrive each month; unused credits don't carry over.`;
  }
  return date ? `Pool of ${amount} credits, good until ${date}.` : `Pool of ${amount} credits.`;
};

/**
 * A signed ledger amount: "−500" (a real minus sign) or "+1,000".
 * @param {number} amount
 */
export const formatSignedCredits = (amount) =>
  amount < 0 ? `−${formatCreditsNumber(-amount)}` : `+${formatCreditsNumber(amount)}`;

/**
 * @typedef {object} LedgerEntry
 * @property {number} amount  Signed.
 * @property {"grant" | "debit" | "refund" | "expire" | string} kind
 * @property {string} label
 * @property {string | null} [listing_address]
 * @property {string | null} [listing_uuid]
 * @property {string} created_at
 * @property {string | null} [expires_at]
 */

/** Kinds that get called out on the row, so a return or an expiry is never
 * mistaken for a spend or a grant.
 * @param {LedgerEntry} entry
 */
export const isCalledOutKind = (entry) => entry.kind === "refund" || entry.kind === "expire";
